import { configureStore, isRejected, Middleware } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import firebaseAuthReducer from '../features/FirebaseAuthSlice.ts'
import signUpWelcomeMessageReducer from '../features/SignUpWelcomeMessageSlice.ts'
import pageTitleReducer from '../features/PageTitleSlice.ts'
import { speechAgendaApi } from '../features/SpeechAgendaApiSlice.ts'
import { getAuth, signOut } from 'firebase/auth'
import globalNotificationModalReducer from '../features/GlobalNotificationModalSlice.ts'
import billingPaywallReducer, {
  showPaywall,
} from '../features/BillingPaywallSlice.ts'
import { getApiErrorCode } from '../utils/ApiError.ts'

interface RejectedPayload {
  status?: number
  error?: string
}

// POST /guestUser also gets 402 but carries no club id in its URL, so fall
// back to the club currently being viewed.
const extractClubId = (url: string | undefined): string | null =>
  url?.match(/\/club\/(\d+)(\/|\?|$)/)?.[1] ?? null

// Billing endpoints render inline errors (the paywall banner / billing page is
// the recovery path); redirecting the whole app to /error on their failures
// would strand the user mid-checkout.
const billingEndpoints = [
  'clubAdminGetBillingStatus',
  'clubAdminCreateCheckoutSession',
  'clubAdminCreatePortalSession',
]

export const rtkQueryErrorLogger: Middleware =
  ({ dispatch }) =>
  (next) =>
  async (action) => {
    // isRejectedWithValue Or isRejected
    if (isRejected(action)) {
      console.log(action) // get all data from rejected request
      // Cast action.payload to the RejectedPayload type
      const payload = action.payload as RejectedPayload
      // if (payload.status === 401) {
      //   //TODO: try auth - then redirect
      //   if (window.location.pathname !== '/login') {
      //     window.location.replace('/login')
      //   }
      //   return next(action)
      // }
      const endpointName = (action.meta.arg as { endpointName: string })
        .endpointName
      if (
        payload.status === 402 &&
        getApiErrorCode(payload) === 'SUBSCRIPTION_REQUIRED'
      ) {
        const requestUrl = (
          action.meta as { baseQueryMeta?: { request?: { url?: string } } }
        ).baseQueryMeta?.request?.url
        const clubId =
          extractClubId(requestUrl) ?? extractClubId(window.location.pathname)
        if (clubId) {
          dispatch(showPaywall(clubId))
        }
        return next(action)
      }
      if (payload.status === 404) {
        //ignore 404 on generateSignInLink
        if (endpointName === 'generateSignInLink') {
          return next(action)
        }
        // A 404 from the digest GET is the "no digest configured yet" state,
        // handled in-component (DigestSettings renders defaults). It must not
        // hijack the page with a redirect to /not-found. The digest send-now
        // 404 (digest deleted after the page loaded) is likewise recovered
        // in-component with an inline "configure and save" message. Enabling the
        // custom template 404s when no draft is stored (e.g. another admin reset
        // it after this page loaded); the editor recovers that inline too.
        if (
          endpointName === 'clubAdminGetDigest' ||
          endpointName === 'clubAdminSendDigestNow' ||
          endpointName === 'clubAdminSetTemplateEnabled'
        ) {
          return next(action)
        }
        // Invalid/tampered token 404s are shown in-component on the public
        // /unsubscribe/:token page, so don't redirect them to /not-found.
        if (endpointName === 'unsubscribeEmail') {
          return next(action)
        }
        if (
          endpointName === 'getUserDetails' &&
          window.location.pathname !== '/user-not-found'
        ) {
          await signOut(getAuth())
          window.location.replace('/user-not-found')
          return next(action)
        }
        if (
          endpointName === 'getClubDetails' &&
          window.location.pathname !== '/club-not-found'
        ) {
          window.location.replace('/club-not-found')
          return next(action)
        }
        if (window.location.pathname !== '/not-found') {
          window.location.replace('/not-found')
          return next(action)
        }
      }
      if (payload.status === 500) {
        if (billingEndpoints.includes(endpointName)) {
          return next(action)
        }
        if (window.location.pathname !== '/error') {
          window.location.replace('/error')
          return next(action)
        }
      }
      if (payload.error === 'TypeError: Failed to fetch') {
        if (window.location.pathname !== '/network-error') {
          window.location.replace('/network-error')
          return next(action)
        }
      }
    }

    return next(action)
  }

export const store = configureStore({
  reducer: {
    signUpWelcome: signUpWelcomeMessageReducer,
    firebaseAuth: firebaseAuthReducer,
    pageTitle: pageTitleReducer,
    globalNotificationModal: globalNotificationModalReducer,
    billingPaywall: billingPaywallReducer,
    [speechAgendaApi.reducerPath]: speechAgendaApi.reducer,
  },
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(speechAgendaApi.middleware)
      .concat(rtkQueryErrorLogger),
})

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch)
